// Radio miniplayer — DOM widget under the next queue during play.
// Replaces the old scrolling now-playing ticker. Transport (prev /
// play-pause / next), the current track title, and a music volume
// slider with a mute toggle. main.ts positions it per-frame so its
// bottom edge aligns with the stats box across window sizes.

import { icon } from '../ui/ui';

export interface MiniPlayerHooks {
  onPrev: () => void;
  onNext: () => void;
  /** Toggle pause/resume; returns the new playing state. */
  onToggle: () => boolean;
  isPlaying: () => boolean;
  getVolume: () => number;
  setVolume: (v: number) => void;
  /** Live analyser over the music chain (null until audio init).
   *  Re-fetched each frame — the underlying node can be rebuilt. */
  getAnalyser: () => AnalyserNode | null;
}

export class MiniPlayer {
  readonly root: HTMLElement;
  private titleEl: HTMLElement;
  private playIconHost: HTMLElement;
  private volume: HTMLInputElement;
  private muteBtn: HTMLButtonElement;
  private hooks: MiniPlayerHooks;
  private lastUnmuted = 0.5;
  private viz: HTMLCanvasElement;
  private vizCtx: CanvasRenderingContext2D;
  private vizData = new Uint8Array(64);
  private vizRaf: number | null = null;

  constructor(hooks: MiniPlayerHooks) {
    this.hooks = hooks;
    this.root = document.createElement('div');
    this.root.id = 'mini-player';
    this.root.style.display = 'none';

    const header = document.createElement('div');
    header.className = 'mini-player-header';
    header.appendChild(icon('now-playing'));
    const headerText = document.createElement('span');
    headerText.textContent = 'now playing';
    header.appendChild(headerText);

    this.titleEl = document.createElement('div');
    this.titleEl.className = 'mini-player-title';
    this.titleEl.textContent = '—';

    // Spectrum bars — sampled from the music chain's analyser while
    // the widget is visible. Sits between the title and the transport.
    this.viz = document.createElement('canvas');
    this.viz.className = 'mini-player-viz';
    this.viz.height = 22;
    this.vizCtx = this.viz.getContext('2d')!;

    const transport = document.createElement('div');
    transport.className = 'mini-player-transport';
    const mkBtn = (name: string, title: string, onClick: () => void): HTMLElement => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'mini-player-btn';
      b.title = title;
      b.appendChild(icon(name));
      b.addEventListener('click', onClick);
      return b;
    };
    transport.appendChild(mkBtn('back', 'previous track', () => this.hooks.onPrev()));
    const playBtn = document.createElement('button');
    playBtn.type = 'button';
    playBtn.className = 'mini-player-btn mini-player-play';
    playBtn.title = 'play / pause';
    this.playIconHost = document.createElement('span');
    this.playIconHost.appendChild(icon(this.hooks.isPlaying() ? 'pause' : 'play'));
    playBtn.appendChild(this.playIconHost);
    playBtn.addEventListener('click', () => {
      const playing = this.hooks.onToggle();
      this.setPlayingIcon(playing);
    });
    transport.appendChild(playBtn);
    transport.appendChild(mkBtn('forward', 'next track', () => this.hooks.onNext()));

    const volRow = document.createElement('div');
    volRow.className = 'mini-player-vol';
    this.muteBtn = document.createElement('button');
    this.muteBtn.type = 'button';
    this.muteBtn.className = 'mini-player-btn mini-player-mute';
    this.muteBtn.title = 'mute music';
    this.muteBtn.appendChild(icon(this.hooks.getVolume() > 0 ? 'sound-on-solid' : 'sound-mute-solid'));
    this.muteBtn.addEventListener('click', () => {
      const v = this.hooks.getVolume();
      if (v > 0) {
        this.lastUnmuted = v;
        this.applyVolume(0);
      } else {
        this.applyVolume(this.lastUnmuted || 0.5);
      }
    });
    this.volume = document.createElement('input');
    this.volume.type = 'range';
    this.volume.min = '0';
    this.volume.max = '1';
    this.volume.step = '0.01';
    this.volume.value = String(this.hooks.getVolume());
    this.volume.className = 'mini-player-slider';
    this.volume.addEventListener('input', () => {
      const v = parseFloat(this.volume.value);
      if (v > 0) this.lastUnmuted = v;
      this.applyVolume(v, /* fromSlider */ true);
    });
    volRow.appendChild(this.muteBtn);
    volRow.appendChild(this.volume);

    this.root.appendChild(header);
    this.root.appendChild(this.titleEl);
    this.root.appendChild(this.viz);
    this.root.appendChild(transport);
    this.root.appendChild(volRow);
  }

  /** Draw one frame of the spectrum bars. The analyser carries only
   *  the radio (sfx live on a separate context), so the bars track
   *  the music: silent / paused decays to a flat baseline. */
  private drawViz = (): void => {
    if (this.root.style.display === 'none') { this.vizRaf = null; return; }
    const ctx = this.vizCtx;
    const w = this.viz.clientWidth;
    if (w > 0 && this.viz.width !== w) this.viz.width = w;
    const h = this.viz.height;
    ctx.clearRect(0, 0, this.viz.width, h);
    const analyser = this.hooks.getAnalyser();
    const BARS = 16;
    const gap = 2;
    const barW = (this.viz.width - gap * (BARS - 1)) / BARS;
    const tint = getComputedStyle(this.root).getPropertyValue('--theme-accent').trim() || '#ff3da6';
    ctx.fillStyle = tint;
    if (analyser) {
      if (this.vizData.length !== analyser.frequencyBinCount) {
        this.vizData = new Uint8Array(analyser.frequencyBinCount);
      }
      analyser.getByteFrequencyData(this.vizData);
      const bins = this.vizData.length;
      for (let i = 0; i < BARS; i++) {
        // Slight low-frequency bias: music energy lives in the lower
        // bins; a sqrt ramp spreads them across the bars evenly.
        const t0 = Math.floor(Math.pow(i / BARS, 1.4) * bins);
        const t1 = Math.max(t0 + 1, Math.floor(Math.pow((i + 1) / BARS, 1.4) * bins));
        let sum = 0;
        for (let b = t0; b < t1; b++) sum += this.vizData[b]!;
        const v = sum / (t1 - t0) / 255;
        const bh = Math.max(1.5, v * (h - 2));
        ctx.globalAlpha = 0.35 + v * 0.65;
        ctx.fillRect(i * (barW + gap), h - bh, barW, bh);
      }
      ctx.globalAlpha = 1;
    } else {
      // No audio yet — flat dim baseline ticks.
      ctx.globalAlpha = 0.25;
      for (let i = 0; i < BARS; i++) ctx.fillRect(i * (barW + gap), h - 1.5, barW, 1.5);
      ctx.globalAlpha = 1;
    }
    this.vizRaf = requestAnimationFrame(this.drawViz);
  };

  private applyVolume(v: number, fromSlider = false): void {
    this.hooks.setVolume(v);
    if (!fromSlider) this.volume.value = String(v);
    while (this.muteBtn.firstChild) this.muteBtn.removeChild(this.muteBtn.firstChild);
    this.muteBtn.appendChild(icon(v > 0 ? 'sound-on-solid' : 'sound-mute-solid'));
  }

  setPlayingIcon(playing: boolean): void {
    while (this.playIconHost.firstChild) this.playIconHost.removeChild(this.playIconHost.firstChild);
    this.playIconHost.appendChild(icon(playing ? 'pause' : 'play'));
  }

  setTitle(title: string | null): void {
    this.titleEl.textContent = title ?? '—';
    this.titleEl.title = title ?? '';
  }

  /** Sync slider + icons with external state (e.g. settings changes). */
  refresh(): void {
    const v = this.hooks.getVolume();
    this.volume.value = String(v);
    while (this.muteBtn.firstChild) this.muteBtn.removeChild(this.muteBtn.firstChild);
    this.muteBtn.appendChild(icon(v > 0 ? 'sound-on-solid' : 'sound-mute-solid'));
    this.setPlayingIcon(this.hooks.isPlaying());
  }

  show(): void {
    if (this.root.style.display === 'none') {
      this.root.style.display = '';
      this.refresh();
      if (this.vizRaf == null) this.vizRaf = requestAnimationFrame(this.drawViz);
    }
  }

  hide(): void {
    this.root.style.display = 'none';
    if (this.vizRaf != null) { cancelAnimationFrame(this.vizRaf); this.vizRaf = null; }
  }

  /** Anchored under the queue; bottom edge meets the stats box bottom. */
  position(left: number, top: number, width: number, height: number): void {
    this.root.style.left = `${Math.round(left)}px`;
    this.root.style.top = `${Math.round(top)}px`;
    this.root.style.width = `${Math.round(Math.max(120, width))}px`;
    this.root.style.height = `${Math.round(Math.max(96, height))}px`;
  }
}
